import {createContext, useContext, useEffect, useReducer} from 'react';
import PropTypes from 'prop-types';
import * as liveswitch from 'fm.liveswitch.tsless';
import {WSState} from './WebSocketProvider';

const appId = process.env.REACT_APP_LIVESWITCH_APP_ID;
const gateway = process.env.REACT_APP_LIVESWITCH_GATEWAY;
const secretKey = process.env.REACT_APP_LIVESWITCH_SECRET_KEY;
const channelId = process.env.REACT_APP_LIVESWITCH_CHANNEL_ID;

if (process.env.REACT_APP_NODE_ENV === 'DEV') {
  liveswitch.Log.setLogLevel(liveswitch.LogLevel.Debug);
} else {
  liveswitch.Log.setLogLevel(liveswitch.LogLevel.None);
}

const client = {
  userId: null,
  deviceId: null,
  isHost: null,
};

const defaultVideoState = {
  inCall: false,
  isMuted: null,
};

const VideoState = createContext(defaultVideoState);
const {Provider} = VideoState;

const turnMicOn = async() => {
  console.log('Turning on mic');
  let config = client.upstreamConnection.getConfig();
  config.setLocalAudioMuted(false);
  client.upstreamConnection.update(config);
};

const turnMicOff = async () => {
  console.log('Turning off mic');
  let config = client.upstreamConnection.getConfig();
  config.setLocalAudioMuted(true);
  client.upstreamConnection.update(config);
};

export const stopStreaming = async ({unregister}) => {
  try {
    await client.layoutManager.unsetLocalView();
  } catch (e) {
    console.error('Failed to unset local view');
  }

  try {
    await client.localMedia.stop();
  } catch (e) {
    console.error('Failed to stop localmedia');
  }

  if (unregister) {
    try {
      await client.liveSwitchClient.unregister();
    } catch (e) {
      console.error('Failed to unregister sender client');
    }
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    /**
     * VIDEO CONNECTION ACTIONS
     * These are connection related actions
     */
    case 'connect':
      return {
        ...state,
        connecting: true,
      };
    case 'connected':
      return {
        ...state,
        connecting: false,
        inCall: true,
        isMuted: false,
      };
    case 'disconnect':
      return {
        ...state,
        inCall: false,
        isMuted: null,
      };

    /**
     * VIDEO TRIGGER ACTIONS
     * These are actions that trigger WS events
     */

    case 'mute':
      turnMicOff();

      return {
        ...state,
        isMuted: true,
      }
    case 'unmute':
      turnMicOn();
      
      return {
        ...state,
        isMuted: false,
      }

    /**
     * DEFAULT
     */
    default:
      return state;
  }
}

const VideoProvider = ({children}) => {
  const {state: wsState} = useContext(WSState);
  const [state, dispatch] = useReducer(
    reducer,
    defaultVideoState,
  );

  /**
   * FUNCTION HELPERS
   */

  const startStreaming = async ({audio, video, id, isHost} = {}) => {
    console.log('Starting call');
  
    client.userId = `user-${id}`;
    client.deviceId = `device-${id}`;
    client.isHost = isHost;
  
    // Create our WebRTC local media context with a typical mono microphone in a possibly noisy environment.
    client.localMedia = new liveswitch.LocalMedia(audio, video);
  
    client.layoutManager = new liveswitch.DomLayoutManager(document.getElementById('player'));
  
    // display local video as if we were receiving the video
    if (client.isHost) {
      client.layoutManager.addRemoteView(client.localMedia.getId(), client.localMedia.getView());
    }
  
    try {
      console.log('Host starting local media');
      await client.localMedia.start();
  
      client.liveSwitchClient = new liveswitch.Client(
        gateway,
        appId,
        client.userId,
        client.deviceId,
        null,
        [],
      );
      let sendClaimChannel = new liveswitch.ChannelClaim(
        channelId,
      );
  
      let sendClaim = [sendClaimChannel];
  
      // save bandwidth
      sendClaimChannel.setDisableMcu(true);
      sendClaimChannel.setDisablePeer(true);
      sendClaimChannel.setDisableSendData(true);
      sendClaimChannel.setCanKick(client.isHost);
  
      let tokenSend = liveswitch.Token.generateClientRegisterToken(
        appId,
        client.liveSwitchClient.getUserId(),
        client.liveSwitchClient.getDeviceId(),
        client.liveSwitchClient.getId(),
        null,
        sendClaim,
        secretKey
      );
      client.sendChannels = await client.liveSwitchClient.register(tokenSend);
  
      console.log('Host send and receive clients registered');
  
      client.sendChannel = client.sendChannels[0];
  
      // gets called when upstream connection starts after current user connects
      client.sendChannel.addOnRemoteUpstreamConnectionOpen(async (remoteConnectionInfo) => {
        console.log('Host addOnRemoteUpstreamConnectionOpen', remoteConnectionInfo);
        await receiveStream({
          channel: client.sendChannel, 
          remoteConnectionInfo: remoteConnectionInfo, 
          layoutManager: client.layoutManager,
        });
      });
  
      // gets called in the scenario where there have been previous remote connections
      client.sendChannel.getRemoteUpstreamConnectionInfos().forEach(async remoteConnectionInfo => {
        console.log('Host getRemoteUpstreamConnectionInfos', remoteConnectionInfo);
        await receiveStream({
          channel: client.sendChannel, 
          remoteConnectionInfo: remoteConnectionInfo, 
          layoutManager: client.layoutManager,
        });
      });
  
      await sendStream({
        channel: client.sendChannel,
        localMedia: client.localMedia,
      });
      
    } catch (exception) {
      console.log("Error: " + exception);
    }
  };

  const sendStream = async ({channel, localMedia} = {}) => {
    console.log('Sending up stream');
    let audioStream = new liveswitch.AudioStream(localMedia);
    let videoStream = new liveswitch.VideoStream(localMedia);
    console.log('Creating sfu upstream');
  
    client.upstreamConnection = channel.createSfuUpstreamConnection(
      audioStream,
      client.isHost ? videoStream : null,
    );
  
    client.upstreamConnection.addOnStateChange((connection) => {
      console.log('Upstream state changed', connection.getState());
      if (connection.getState() == liveswitch.ConnectionState.Connected) {
        console.log("Connected Sender to Channel");
      }
    });
  
    console.log('Opening upstream connection');
    client.upstreamConnection.open();
  };
  
  const receiveStream = async ({channel, remoteConnectionInfo, layoutManager} = {}) => {
    console.log('Receiving stream', channel, remoteConnectionInfo, layoutManager);
    let remoteMedia = new liveswitch.RemoteMedia();
  
    remoteMedia.setAudioMuted(false);
    remoteMedia.setVideoMuted(false);
  
    console.log('Adding remote view', layoutManager);
  
    let audioStream = new liveswitch.AudioStream(remoteMedia);
    let videoStream = null;
    if (remoteConnectionInfo.getHasVideo()) {
      videoStream = await new liveswitch.VideoStream(remoteMedia)
      layoutManager.addRemoteView(remoteMedia.getId(), remoteMedia.getView());
    }
  
    console.log('Creating downstream');
    client.downstreamConnection = channel.createSfuDownstreamConnection(
      remoteConnectionInfo,
      audioStream,
      videoStream,
    );
  
    await client.downstreamConnection.addOnStateChange(async (connection) => {
      console.log('Downstream status changed', connection.getState());
      if (connection.getState() == liveswitch.ConnectionState.Connected) {
        console.log("Connected Receiver to Channel");
      } else if (connection.getState() == liveswitch.ConnectionState.Closed) {
        console.log("Connection closed");
 
        if (remoteConnectionInfo.getHasVideo()) {
          await stopStreaming({unregister: false});
          
          dispatch({type: 'disconnect'});
        }
      }
    });
  
    client.downstreamConnection.open();
  };

  useEffect(() => {
    if (state?.connecting) {
      startStreaming({
        audio: true, 
        video: wsState.isHost ? { height: 200, width: 200 } : false, 
        id: wsState.connectionId,
        isHost: wsState.isHost,
      })
      .then(() => dispatch({type: 'connected'}));
    }
  }, [state.connecting]);

  return (
    <Provider value={{state, dispatch}}>
      {children}
    </Provider>
  );
};

VideoProvider.propTypes = {
  children: PropTypes.node,
};

export {
  VideoProvider,
  VideoState,
};
